import React from 'react';
import { X, Search, Package, Check, Clock, Truck, Home, AlertCircle } from 'lucide-react';
import { Order, OrderStatus } from '../types';

interface OrderTrackingModalProps {
  isOpen: boolean;
  onClose: () => void;
  orders: Order[];
  searchId: string;
  setSearchId: (id: string) => void;
}

export const OrderTrackingModal: React.FC<OrderTrackingModalProps> = ({ 
  isOpen, onClose, orders, searchId, setSearchId 
}) => {
  if (!isOpen) return null;

  const foundOrder = orders.find(o => o.id === searchId);

  const steps: { status: OrderStatus; label: string; icon: React.ReactNode }[] = [
    { status: 'Pending', label: 'Placed', icon: <Package size={14} /> },
    { status: 'Approved', label: 'Approved', icon: <Check size={14} /> },
    { status: 'Processing', label: 'Processing', icon: <Clock size={14} /> },
    { status: 'Shipped', label: 'Shipped', icon: <Truck size={14} /> },
    { status: 'Delivered', label: 'Delivered', icon: <Home size={14} /> },
  ];

  const getStepStatus = (stepIndex: number, currentStatus: OrderStatus) => {
    if (currentStatus === 'Declined') return 'error';
    const statusOrder: OrderStatus[] = ['Pending', 'Approved', 'Processing', 'Shipped', 'Delivered'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    
    if (currentIndex === -1) return 'waiting';
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'waiting';
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black bg-opacity-75 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 animate-slide-in-left relative shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={24} />
        </button>
        
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-900">
            <Truck className="text-blue-600" /> Track Order
        </h2>

        {/* Search Section */}
        <div className="relative mb-8">
            <input 
                type="text" 
                placeholder="Enter Order ID (e.g. 173849)" 
                className="w-full border-2 border-gray-200 rounded-xl px-5 py-3 pl-12 focus:border-blue-500 focus:outline-none transition-colors"
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                autoFocus
            />
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        </div>

        {/* Results */}
        {foundOrder ? (
            <div className="animate-fade-in">
                <div className="bg-gray-50 p-5 rounded-xl mb-8 border border-gray-100">
                    <div className="flex justify-between items-center mb-3">
                        <span className="font-bold text-lg text-gray-900">Order #{foundOrder.id}</span>
                        <span className="text-xs font-medium bg-white px-2 py-1 rounded border text-gray-500">
                            {new Date(foundOrder.date).toLocaleDateString()}
                        </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Items: {foundOrder.items.length}</span>
                        <span>Total: <span className="font-bold text-gray-900">Rs. {foundOrder.total.toLocaleString()}</span></span>
                    </div>
                    <div className="text-sm text-gray-600 truncate">
                        To: {foundOrder.customerName}
                    </div>
                </div>

                {foundOrder.status === 'Declined' ? (
                     <div className="bg-red-50 text-red-700 p-6 rounded-xl text-center border border-red-100 flex flex-col items-center gap-2">
                        <div className="bg-red-100 p-3 rounded-full mb-2">
                            <AlertCircle size={32} />
                        </div>
                        <h3 className="font-bold text-lg">Order Declined</h3>
                        <p className="text-sm opacity-90">We're sorry, this order could not be processed. Please contact support.</p>
                     </div>
                ) : (
                    <div className="relative mt-4 px-2">
                         {/* Progress Bar Background */}
                        <div className="absolute top-5 left-4 right-4 h-1 bg-gray-200 -z-10 rounded-full"></div>
                        
                        {/* Active Progress Bar */}
                        <div 
                            className="absolute top-5 left-4 h-1 bg-green-500 -z-10 rounded-full transition-all duration-700 ease-out"
                            style={{ 
                                width: `calc(${(steps.findIndex(s => s.status === foundOrder.status) / (steps.length - 1)) * 100}% - 2rem)` 
                            }}
                        ></div>

                        <div className="flex justify-between items-start">
                            {steps.map((step, idx) => {
                                const statusType = getStepStatus(idx, foundOrder.status);
                                const isCompleted = statusType === 'completed';
                                const isCurrent = statusType === 'current';
                                
                                return (
                                    <div key={step.status} className="flex flex-col items-center gap-3 w-16">
                                        <div 
                                            className={`w-10 h-10 rounded-full flex items-center justify-center text-white transition-all duration-500 shadow-sm z-10 ${
                                                isCompleted || isCurrent ? 'bg-green-500 scale-110 shadow-green-200' : 'bg-gray-200 text-gray-400'
                                            }`}
                                        >
                                            {step.icon}
                                        </div>
                                        <div className="text-center">
                                            <span 
                                                className={`text-[10px] font-bold uppercase tracking-wider block transition-colors duration-300 ${
                                                    isCompleted || isCurrent ? 'text-green-600' : 'text-gray-400'
                                                }`}
                                            >
                                                {step.label}
                                            </span>
                                            {isCurrent && (
                                                <span className="text-[9px] text-green-500 font-medium animate-pulse">
                                                    Current
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        ) : searchId.length > 0 ? (
            <div className="text-center text-gray-400 py-12 flex flex-col items-center">
                <Search size={48} className="mb-4 opacity-20" />
                <p className="font-medium text-gray-500">Order not found</p>
                <p className="text-xs mt-1">Please check the ID and try again.</p>
            </div>
        ) : (
             <div className="text-center text-gray-400 py-12 flex flex-col items-center">
                <Package size={48} className="mb-4 opacity-20" />
                <p className="text-sm">Enter your Order ID above to check its status.</p>
            </div>
        )}
      </div>
    </div>
  );
};